import React, { type FC } from "react";

import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRecoilCallback, useRecoilValue } from "recoil";

import {
  Flex,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  NoResults,
  PANEL_SPACING_VARIANT_SECONDARY,
  Panel,
  TEXT_VARIANT_STANDARD,
  Text,
} from "mailup-web-components";

import { evalCondition, notifyParent } from "mailup-react-helpers";

import InputStatus from "Pages/RecipientsList/components/filters/InputStatus";
import RecipientsCounter from "Pages/RecipientsList/components/filters/RecipientsCounter";

import Divider from "Components/Divider";
import ActionAddRecipient from "Pages/RecipientsList/components/results/ActionAddRecipient";
import ActionAddSegment from "Pages/RecipientsList/components/results/ActionAddSegment";
import ActionAddSegmentUpselling from "Pages/RecipientsList/components/results/ActionAddSegmentUpselling";
import ActionChangeColumns from "Pages/RecipientsList/components/results/ActionChangeColumns";
import BatchActions from "Pages/RecipientsList/components/results/BatchActions";

import RenderWhen from "Components/RenderWhen";
import Pager from "Pages/RecipientsList/components/results/Pager";
import Results from "Pages/RecipientsList/components/results/Results";
import { MAX_RECIPIENTS_EXPORT_XLSX } from "Pages/RecipientsList/model";
import { fetchResultsCallback } from "Pages/RecipientsList/recoil/callbacks/callbacks";
import {
  emptyStateState,
  paginationState,
  resultsState,
  selectionCountState,
} from "Pages/RecipientsList/recoil/state/results";

const BoxResults: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const items = useRecoilValue(resultsState);

  const selectionCount = useRecoilValue(selectionCountState);
  const { forcedRecipient } = useRecoilValue(paginationState);
  const isEmptyState = useRecoilValue(emptyStateState);

  const fetchResults = useRecoilCallback(fetchResultsCallback, []);

  const handleLoadRecipients = (): void => {
    void fetchResults({});
  };

  const handleImportClick = (): void => {
    navigate("/recipients/import");
  };

  const handleCreateLPClick = (): void => {
    const route = `databasebuilding_app_redirector.aspx?r=${encodeURIComponent(
      "/databasebuilding/landingpage/templates/list?tags=HASFORM",
    )}`;
    notifyParent("redirectMainFrame", [route]);
  };

  const hasAdvSegment = evalCondition("co:ADVSEGMENT") as boolean;

  return (
    <Panel margin="0">
      <Flex flexDirection="column" gap="24px">
        <Flex flexDirection="column" gap="16px">
          <InputStatus />

          <Flex alignItems="center" justifyContent="space-between">
            <RecipientsCounter />

            <Flex alignItems="center" gap="24px">
              <ActionChangeColumns />
              <RenderWhen condition={evalCondition("NOT uo:PRIVACYBLOCK")}>
                <ActionAddRecipient />
              </RenderWhen>
            </Flex>
          </Flex>
        </Flex>

        <Divider />

        <RenderWhen condition={isEmptyState}>
          <NoResults
            margin="0"
            text={intl.formatMessage(
              {
                id: "Load.RecipientsList.EmptyStateMessage",
              },
              {
                link1: (chunks) => (
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    onClick={handleImportClick}
                    data-mu-test="recipients-list__empty-state__import"
                  >
                    {chunks}
                  </Link>
                ),
                link2: (chunks) => (
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    onClick={handleCreateLPClick}
                    data-mu-test="recipients-list__empty-state__lp"
                  >
                    {chunks}
                  </Link>
                ),
              },
            )}
          />
        </RenderWhen>

        <RenderWhen
          condition={!isEmptyState && items.length === 0 && forcedRecipient}
        >
          <Panel margin="0" spacingVariant={PANEL_SPACING_VARIANT_SECONDARY}>
            <Flex justifyContent="center" gap="4px">
              {intl.formatMessage(
                { id: "Load.SearchRecipients.LoadRecipients" },
                {
                  link: (chunks: any) => (
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      data-mu-test="load-recipient"
                      onClick={handleLoadRecipients}
                    >
                      {chunks}
                    </Link>
                  ),
                },
              )}
            </Flex>
          </Panel>
        </RenderWhen>

        <RenderWhen
          condition={!isEmptyState && items.length === 0 && !forcedRecipient}
        >
          <NoResults
            margin="0"
            text={`${intl.formatMessage({
              id: "Load.SearchRecipients.noResultsTitle",
            })} ${intl.formatMessage({
              id: "Load.SearchRecipients.noResultsAction",
            })}`}
          />
        </RenderWhen>

        <RenderWhen condition={items.length > 0}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center" justifyContent="start" gap="24px">
              <BatchActions direction="down" />

              <Text variant={TEXT_VARIANT_STANDARD}>
                {intl.formatMessage(
                  {
                    id: "Load.RecipientsList.RecipientsSelectionCount",
                  },
                  {
                    count: selectionCount,
                    b: (chunks: any) => <strong>{chunks}</strong>,
                  },
                )}

                {selectionCount > MAX_RECIPIENTS_EXPORT_XLSX && (
                  <>
                    {" "}
                    (
                    {intl.formatMessage({
                      id: "Load.RecipientsList.MaxRecipientsExportXLSXExceeded",
                    })}
                    )
                  </>
                )}
              </Text>
            </Flex>

            <RenderWhen condition={hasAdvSegment}>
              <ActionAddSegment />
            </RenderWhen>

            <RenderWhen condition={!hasAdvSegment}>
              <ActionAddSegmentUpselling />
            </RenderWhen>
          </Flex>

          <Results />

          <Pager />
        </RenderWhen>
      </Flex>
    </Panel>
  );
};

export default BoxResults;
